import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';

const API_BASE_URL = 'https://api.iva-store.com';

const CartSidebar = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/60x60?text=No+Image';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE_URL}${imageUrl}`;
  };

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleClearCart = () => {
    clearCart();
    toast.success('تم إفراغ السلة بنجاح');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col" dir="rtl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">سلة التسوق</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-gray-500 text-lg mb-4">السلة فارغة</p>
            <button
              onClick={onClose}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              تصفح المنتجات
            </button>
          </div>
        ) : (
          <>
            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cartItems.map(item => {
                const leftPower = item.leftEyePower || (item.selectedPower && item.selectedPower !== 'yes' && item.selectedPower !== 'no' ? item.selectedPower : null);
                const rightPower = item.rightEyePower || (item.selectedPower && item.selectedPower !== 'yes' && item.selectedPower !== 'no' ? item.selectedPower : null);
                const powerKey = `L${leftPower || 'none'}::R${rightPower || 'none'}`;
                return (
                <div
                  key={`${item.id}-${powerKey}`}
                  className="flex gap-3 bg-gray-50 p-3 rounded-lg"
                >
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">
                      {item.name}
                    </h3>
                    {(leftPower || rightPower) && (
                      <div className="text-xs text-blue-600 mt-1 space-y-0.5">
                        {rightPower && <div>العين اليمنى: {rightPower}</div>}
                        {leftPower && <div>العين اليسرى: {leftPower}</div>}
                      </div>
                    )}
                    {item.part_number && (
                      <p className="text-xs text-gray-500">رقم القطعة: {item.part_number}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-gray-300 rounded">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1, item.leftEyePower, item.rightEyePower, item.selectedPower)}
                          className="px-2 py-1 hover:bg-gray-100"
                        >
                          -
                        </button>
                        <span className="px-3 py-1 border-x border-gray-300 text-sm">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1, item.leftEyePower, item.rightEyePower, item.selectedPower)}
                          className="px-2 py-1 hover:bg-gray-100"
                        >
                          +
                        </button>
                      </div>
                      <p className="font-bold text-primary">{(parseFloat(item.price || item.final_price || item.sell_price || 0) || 0).toLocaleString()} د.ع</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id, item.leftEyePower, item.rightEyePower, item.selectedPower)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t p-4 space-y-3">
              {/* Price Breakdown */}
              <div className="space-y-2 pb-3 border-b border-gray-200">
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>المجموع الفرعي:</span>
                  <span className="font-semibold">{(getTotalPrice() || 0).toLocaleString()} د.ع</span>
                </div>
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>رسوم التوصيل:</span>
                  <span className="font-semibold">التوصيل مجاني</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold pt-2">
                  <span>المجموع الكلي:</span>
                  <span className="text-primary">{(getTotalPrice() || 0).toLocaleString()} د.ع</span>
                </div>
              </div>
              
              {/* Coupon hint */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                <p className="text-xs text-blue-700">
                  💰 لديك كود خصم؟ يمكنك تطبيقه عند الدفع
                </p>
              </div>
              
              <button
                onClick={handleCheckout}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-blue-700 font-semibold transition-colors"
              >
                إتمام الشراء
              </button>
              
              <button
                onClick={handleClearCart}
                className="w-full bg-red-100 text-red-600 py-2 rounded-lg hover:bg-red-200 transition-colors"
              >
                إفراغ السلة
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
